import type { z } from "zod";

import { useUser } from "@clerk/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { onboardingPlayerSchema } from "@teamz/common";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-number-input/input";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ErrorField, RootField, TextField } from "@/components/ui/text-field";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { routes } from "@/routes";

import { CountryCombobox } from "../country-combobox";
import { Input } from "../ui/input";

const firstStepSchema = onboardingPlayerSchema.pick({
  phone: true,
  firstName: true,
  lastName: true,
});
type FirstStepSchema = z.infer<typeof firstStepSchema>;

const secondStepSchema = onboardingPlayerSchema.pick({
  birthdate: true,
  gender: true,
  nationalityCode: true,
});
type SecondStepSchema = z.infer<typeof secondStepSchema>;

function OnboardingFirstStepForm({ setData, data }: { data?: FirstStepSchema; setData: (data: FirstStepSchema) => void }) {
  const { t } = useTranslation(["common"]);
  const firstStepForm = useForm<FirstStepSchema>({
    resolver: zodResolver(firstStepSchema),
    defaultValues: data,
  });

  const onSubmit = firstStepForm.handleSubmit((data) => {
    setData(data);
  });

  const firstFormErrors = firstStepForm.formState.errors;

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4 px-4">
      <TextField
        label={t("common:firstname")}
        errorMessage={tDyn(t, firstFormErrors.firstName?.message)}
        {...firstStepForm.register("firstName")}
      />
      <TextField
        label={t("common:lastname")}
        errorMessage={tDyn(t, firstFormErrors.lastName?.message)}
        {...firstStepForm.register("lastName")}
      />

      <Controller
        control={firstStepForm.control}
        name="phone"
        render={({ field, fieldState }) => (
          <RootField>
            <Label>{t("common:phone")}</Label>
            <PhoneInput
              inputComponent={Input}
              placeholder="+33 6 12 34 56 78"
              onChange={(value) => {
                if (!value || value === "+") {
                  field.onChange("");
                  return;
                }
                field.onChange(value);
              }}
              value={field.value?.replace(/\s/g, "") || ""}
            />
            {fieldState.error?.message && (
              <ErrorField>{tDyn(t, `common:${fieldState.error.message}`)}</ErrorField>
            )}
          </RootField>
        )}
      />

      <div className="flex w-full items-center justify-center gap-3 px-4 pb-4">
        <Button
          type="submit"
          className="w-44"
          loading={firstStepForm.formState.isSubmitting}
        >
          {t("common:next")}
        </Button>
      </div>
    </form>
  );
}

function OnboardingSecondStepForm({ firstStepData, getBack }: { firstStepData: FirstStepSchema; getBack: () => void }) {
  const { t } = useTranslation(["common"]);
  const navigate = useNavigate();
  const { user } = useUser();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.player.createPlayer.mutationOptions({
    onSuccess: async () => {
      await user?.reload();
      // `getMe` est déjà en cache avec hasProfile: false (garde d'onboarding) :
      // sans invalidation, `Layout` renverrait aussitôt sur ce formulaire.
      await queryClient.invalidateQueries(trpc.user.getMe.queryFilter());
      navigate(routes.profile);
    },
  }));

  const secondStepForm = useForm<SecondStepSchema>({
    resolver: zodResolver(secondStepSchema),
  });

  const onSubmit = secondStepForm.handleSubmit((data) => {
    mutate({ ...firstStepData, ...data });
  });

  const today = new Date().toISOString().split("T")[0];

  const secondFormErrors = secondStepForm.formState.errors;

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4 px-4">
      <TextField
        label={t("common:birthdate")}
        max={today}
        type="date"
        errorMessage={tDyn(t, secondFormErrors.birthdate?.message)}
        {...secondStepForm.register("birthdate")}
      />

      <Controller
        control={secondStepForm.control}
        name="gender"
        render={({ field, fieldState }) => (
          <RootField>
            <Label>{t("common:gender")}</Label>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="male">{t("common:man")}</SelectItem>
                  <SelectItem value="female">{t("common:woman")}</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            {fieldState.error?.message && (
              <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
            )}
          </RootField>
        )}
      />

      <Controller
        control={secondStepForm.control}
        name="nationalityCode"
        render={({ field, fieldState }) => (
          <RootField>
            <Label>{t("common:nationality-country")}</Label>
            <CountryCombobox field={field} />
            {fieldState.error && (
              <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
            )}
          </RootField>
        )}
      />

      <div className="flex w-full items-center justify-center gap-3 px-4 pb-4">
        <Button
          type="button"
          variant="dark"
          onClick={getBack}
          className="w-full"
        >
          {t("common:back")}
        </Button>
        <Button
          type="submit"
          loading={isPending || secondStepForm.formState.isSubmitting}
          className="w-full"
        >
          {t("common:save")}
        </Button>
      </div>
    </form>
  );
}

export function PlayerOnboardingForm() {
  const [step, setStep] = useState<1 | 2>(1);
  const [playerInformationsFirstStepData, setPlayerInformationsFirstStepData] = useState<FirstStepSchema>();
  const { t } = useTranslation(["common"]);

  return (
    <div
      className="flex w-96 flex-col gap-6 rounded-2xl bg-neutral-700"
    >
      <div className="border-b border-neutral-500 p-4 text-center">
        <span className="text-base font-semibold">
          {t("common:player-onboarding")}
          {" "}
          (
          {step}
          /2)
        </span>
      </div>
      {step === 1 && (
        <OnboardingFirstStepForm
          data={playerInformationsFirstStepData}
          setData={(data) => {
            setPlayerInformationsFirstStepData(data);
            setStep(2);
          }}
        />
      )}
      {(step === 2 && playerInformationsFirstStepData)
      && <OnboardingSecondStepForm firstStepData={playerInformationsFirstStepData} getBack={() => setStep(1)} />}
    </div>
  );
}
